import { PayloadAction } from '@reduxjs/toolkit';
import { put, select, takeLatest } from 'redux-saga/effects';
import { ProjectId, TaskId } from 'api/models';
import { TaskAPI, Task } from 'api/tasks';
import { FetchStatus } from 'data/core';
import { updateTaskPrivacyInGanttAction } from 'data/projects/actions';
import { queryClient } from 'queries';
import { getProjectTasksQueryKey } from 'queries/common';
import {
  UpdateTaskActionPayloadOptions,
  updateTaskPrivacySagaAction,
} from '../actions';
import { selectTask } from '../selectors';
import { saveTasks, updateTaskPrivacy } from '../slice';

function* updateTaskPrivacySaga({
  payload,
}: PayloadAction<{
  taskId: number;
  private: boolean;
  projectId: number;
  options?: UpdateTaskActionPayloadOptions;
}>): Generator {
  if (payload) {
    const { taskId, private: isPrivate, projectId, options } = payload;

    const oldTaskState = (yield select(
      selectTask(TaskId(projectId, taskId)),
    )) as Task | undefined;

    if (!options?.skipStoreUpdate) {
      yield put(updateTaskPrivacy(payload));
    }

    try {
      yield TaskAPI.updatePrivacy(taskId, {
        private: isPrivate,
      });

      yield put(
        updateTaskPrivacyInGanttAction({
          taskId,
          private: isPrivate,
        }),
      );
    } catch {
      if (oldTaskState) {
        if (options?.rejectCallback) {
          options.rejectCallback(oldTaskState);
        }
        if (!options?.skipStoreUpdate) {
          yield put(
            saveTasks({
              data: [oldTaskState],
              fetchStatus: FetchStatus.Loaded,
            }),
          );
        }
        yield put(
          updateTaskPrivacyInGanttAction({
            taskId,
            private: oldTaskState.private,
          }),
        );
      }
    }

    yield queryClient.refetchQueries({
      predicate: (query) => {
        return (
          query.queryHash.includes('task-detail') &&
          query?.queryHash.includes(taskId.toString())
        );
      },
    });
    if (projectId) {
      yield queryClient.refetchQueries(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );
    }
  }
}

export function* watchUpdateTaskPrivacySaga(): Generator {
  yield takeLatest(updateTaskPrivacySagaAction.type, updateTaskPrivacySaga);
}
